// Inert until batch4 wires the recoverable route, UI subscriptions and hydration.
export const isSessionMarker = value => typeof value === 'string' && /^(?:[a-f0-9]{32}|[a-f0-9]{64})$/i.test(value)

export function sessionError(code, status) {
  const error = new Error('No se pudo completar la sesión.')
  const allowed = ['SESSION_CHANGED', 'SESSION_OBSOLETE', 'SESSION_INVALID_RESPONSE',
    'SESSION_PUBLICATION_FAILED', 'SESSION_CANCELLED', 'SESSION_FAILED', 'SESSION_DESTINATION_DENIED',
    'SESSION_MUTATION_REQUIRED', 'SESSION_REQUEST_FAILED', 'COORDINATION_UNAVAILABLE',
    'ENTRY_UNAVAILABLE', 'ENTRY_CONSUMED', 'STORAGE_UNAVAILABLE', 'REDIRECT_AUTH_DISABLED']
  error.code = allowed.includes(code) ? code : 'SESSION_FAILED'
  if (typeof status === 'number') error.status = status
  return error
}

export function createBrowserSessionPort(browser) {
  return {
    readMarker() {
      const matches = browser.document.cookie.split(';').map(value => value.trim())
        .filter(value => value.indexOf('auth_token=') === 0)
      if (matches.length !== 1) return null
      const marker = decodeURIComponent(matches[0].slice('auth_token='.length))
      return isSessionMarker(marker) ? marker : null
    },
    removeMarker() {
      browser.document.cookie = 'auth_token=; Max-Age=0; Path=/'
    },
    writeMarker(marker) {
      browser.document.cookie = `auth_token=${encodeURIComponent(marker)}; Path=/`
    },
    resetAccount() {
      // Account drafts are sessionStorage, not the localStorage theme preference.
      browser.sessionStorage.removeItem('promoAs')
      browser.sessionStorage.removeItem('promoSettings')
    },
  }
}

export function createSessionTransition(port) {
  let epoch = 0
  let marker = null
  let owner = null
  let phase = 'anonymous'
  let peerCheck = null
  let snapshot = Object.freeze({ epoch, phase })
  const listeners = new Set()
  const cancellations = new Set()
  const notify = () => {
    snapshot = Object.freeze({ epoch, phase })
    listeners.forEach(listener => { try { listener(snapshot) } catch (_) {} })
  }
  const read = () => {
    try { return port.readMarker() } catch (_) { return null }
  }
  const reset = () => {
    try { port.resetAccount(); return true } catch (_) { return false }
  }
  const advance = next => {
    epoch += 1
    owner = null
    marker = null
    phase = next
    cancellations.forEach(cancel => { try { cancel() } catch (_) {} })
    cancellations.clear()
    if (!reset()) phase = 'blocked'
    notify()
  }
  const session = {
    getSnapshot: () => snapshot,
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener) },
    setPeerCheck(check) {
      peerCheck = check
      return () => { if (peerCheck === check) peerCheck = null }
    },
    readMarker: read,
    // Explicit startup adoption only; never called by peer events or protected dispatch.
    initialize() {
      if (epoch || phase !== 'anonymous') return false
      marker = read()
      phase = marker ? 'authenticated' : 'anonymous'
      notify()
      return !!marker
    },
    invalidate() { advance('anonymous') },
    check() {
      if (peerCheck) peerCheck()
      if (phase === 'authenticated' && (!marker || read() !== marker)) advance('anonymous')
      return phase === 'authenticated'
    },
    captureEntry() {
      const previousMarker = marker || read()
      advance('transitioning')
      try {
        // Never clear a marker that changed since this synchronous capture.
        if (read() === previousMarker) port.removeMarker()
        if (read()) throw sessionError('SESSION_CHANGED')
      } catch (_) { phase = 'blocked'; notify() }
      return previousMarker
    },
    begin(previousMarker) {
      // Called ONLY inside the mutation lock. A queued explicit login can replace
      // the current cookie, but must not adopt that account for protected work.
      const binding = read() || previousMarker || null
      advance('transitioning')
      if (phase === 'blocked') throw sessionError('STORAGE_UNAVAILABLE')
      try {
        port.removeMarker()
        if (read()) throw sessionError('SESSION_CHANGED')
      } catch (_) { phase = 'blocked'; notify(); throw sessionError('SESSION_CHANGED') }
      const attempt = Object.freeze({ epoch, binding: () => binding })
      owner = attempt
      return attempt
    },
    owns: attempt => owner === attempt && phase === 'transitioning',
    commit(attempt, value) {
      if (!session.owns(attempt)) throw sessionError('SESSION_OBSOLETE')
      if (value !== null && !isSessionMarker(value)) throw sessionError('SESSION_INVALID_RESPONSE')
      try {
        if (value) port.writeMarker(value)
        else port.removeMarker()
        if (read() !== value) throw sessionError('SESSION_CHANGED')
      } catch (_) { session.fail(attempt); throw sessionError('SESSION_PUBLICATION_FAILED') }
      owner = null
      marker = value
      phase = marker ? 'authenticated' : 'anonymous'
      notify()
    },
    restoreDisabledRedirect(attempt) {
      // The backend guarantees this exact disabled response before lifecycle
      // mutation. Never restore after a lost owner, or from any other failure.
      if (!session.owns(attempt)) return false
      const value = attempt.binding()
      if (!isSessionMarker(value)) return false
      try {
        if (read()) return false
        port.writeMarker(value)
        if (read() !== value) throw sessionError('SESSION_CHANGED')
      } catch (_) { return false }
      owner = null
      marker = value
      phase = 'authenticated'
      notify()
      return true
    },
    fail(attempt) {
      // Failure/peer notifications NEVER delete another tab's shared cookie.
      if (session.owns(attempt)) advance('anonymous')
    },
    request(attempt) {
      if (attempt ? !session.owns(attempt) : !session.check()) throw sessionError('SESSION_OBSOLETE')
      const captured = epoch
      const binding = attempt ? attempt.binding() : marker
      return {
        binding: () => binding,
        current: () => epoch === captured && (attempt ? session.owns(attempt) : session.check()),
        cancelWith(cancel) {
          if (epoch !== captured) { cancel(); return () => {} }
          cancellations.add(cancel)
          return () => cancellations.delete(cancel)
        },
      }
    },
  }
  return session
}
